import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { ApiResponse, getResponseData } from '../models/api-response.model';
import { Product, ProductDetail } from '../models/product.model';
import { normalizeProduct, normalizeProductList } from '../utils/product-normalize.util';

const SEARCH_PAGE_SIZE = 24;

export interface CategoryProductsQueryOptions {
  sortBy?: 'price' | 'name' | 'recommended';
  sortOrder?: 'asc' | 'desc';
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  brands?: string[];
  /** When true, API returns products for the category and all descendant categories in one call. */
  includeDescendants?: boolean;
}

/**
 * Catalog product APIs. Personalization (wishlist/cart flags) uses the Bearer JWT /
 * guest session header — never a query `userId`.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly branchId = environment.branchId;
  /** 0 = Store, 1 = Electronics. From environment. */
  private readonly platform = (environment as { platform?: number }).platform ?? 0;

  constructor(private api: ApiService) {}

  getBestSellers(_userId?: string): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {
      branchId: this.branchId,
      platform: this.platform,
    };
    return this.api.get<Product[]>('Products/GetBestSellerProducts', queryParams).pipe(
      map((response) => normalizeProductList(getResponseData(response, [])))
    );
  }

  getAllProducts(_userId?: string): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {
      branchId: this.branchId,
      platform: this.platform,
    };
    return this.api.get<Product[]>('Stock/GetProducts', queryParams).pipe(
      map((response) => normalizeProductList(getResponseData(response, [])))
    );
  }

  getRecentlyAddedProducts(
    _userId?: string,
    limit = 12,
    excludeIds: number[] = []
  ): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {
      branchId: this.branchId,
      platform: this.platform,
      limit,
    };
    if (excludeIds.length) queryParams['excludeIds'] = excludeIds.join(',');
    return this.api.get<Product[]>('Products/GetRecentlyAddedProducts', queryParams).pipe(
      map((response) => normalizeProductList(getResponseData(response, [])))
    );
  }

  getFeaturedProducts(_userId?: string): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {
      branchId: this.branchId,
      platform: this.platform,
    };
    return this.api.get<Product[]>('Products/GetFeaturedProducts', queryParams).pipe(
      map((response) => normalizeProductList(getResponseData(response, [])))
    );
  }

  getDiscounts(_userId?: string): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {
      branchId: this.branchId,
      platform: this.platform,
    };
    return this.api.get<Product[]>('Products/GetProductsWithDiscount', queryParams).pipe(
      map((response) => normalizeProductList(getResponseData(response, [])))
    );
  }

  getProductsByIds(ids: number[], _userId?: string): Observable<Product[]> {
    if (!ids?.length) return of([]);
    const idString = ids.join(',');
    const queryParams: Record<string, string | number | boolean> = {
      ids: idString,
      branchId: this.branchId,
      platform: this.platform,
    };
    return this.api.get<Product[]>('Products/GetProductsByIds', queryParams).pipe(
      map((response) => {
        const products = normalizeProductList(getResponseData(response, []));
        const order = new Map(ids.map((id, index) => [id, index]));
        return [...products].sort(
          (a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER)
        );
      })
    );
  }

  getProductsByCategory(
    categoryId: number,
    options?: CategoryProductsQueryOptions,
    _userId?: string
  ): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {
      categoryId,
      branchId: this.branchId,
      platform: this.platform,
    };

    if (options?.sortBy) queryParams['sortBy'] = options.sortBy;
    if (options?.sortOrder) queryParams['sortOrder'] = options.sortOrder;
    if (typeof options?.minPrice === 'number') queryParams['minPrice'] = options.minPrice;
    if (typeof options?.maxPrice === 'number') queryParams['maxPrice'] = options.maxPrice;
    if (typeof options?.inStock === 'boolean') queryParams['inStock'] = options.inStock;
    if (options?.brands?.length) queryParams['brands'] = options.brands.join(',');
    if (options?.includeDescendants) queryParams['includeDescendants'] = true;

    return this.api
      .get<Product[]>('Products/GetProductsByCategory', queryParams)
      .pipe(
        map((response) => {
          const data = getResponseData(response, []);
          if (Array.isArray(data)) {
            return normalizeProductList(data);
          }
          const r = response as ApiResponse<Product[]> & { products?: Product[]; Products?: Product[] };
          const alt = r.products ?? r.Products;
          return normalizeProductList(Array.isArray(alt) ? alt : []);
        })
      );
  }

  getProductById(productId: number, _userId?: string): Observable<ProductDetail> {
    const params: Record<string, string | number> = {
      Id: productId,
      branchId: this.branchId,
      platform: this.platform,
    };
    return this.api.get<ProductDetail>('Products/GetProductById', params).pipe(
      map((response) => normalizeProduct<ProductDetail>(getResponseData(response, {} as ProductDetail)))
    );
  }

  searchProducts(
    searchTerm: string,
    pageNumber = 1,
    pageSize = SEARCH_PAGE_SIZE,
    _userId?: string
  ): Observable<Product[]> {
    const query = searchTerm.trim().replace(/\s+/g, ' ');
    if (!query) return of([]);

    const params: Record<string, string | number> = {
      searchTerm: query,
      pageNumber,
      pageSize,
      branchId: this.branchId,
      platform: this.platform,
    };

    return this.api.get<PaginatedProducts>('Products/search', params).pipe(
      map((response) => getResponseData(response, { data: [] })),
      map((paged) => normalizeProductList(paged.data ?? paged.Data ?? []))
    );
  }
}

interface PaginatedProducts {
  data?: Product[];
  Data?: Product[];
}
